import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {createSelector} from 'reselect';

import {useMemoizedSelector} from '../../state/hooks';
import {useViewAction} from '../../state/views/hooks';
import {update as updateRunSet} from '../../state/views/runSet/actions';
import type {Ref as RunSetRef} from '../../state/views/runSet/types';
import {
  type ConfigV2,
  EMPTY_CONFIG as EMPTY_TABLE_SETTINGS,
} from '../../util/runfeed';
import {makeRunSetSelector} from './runSetSelectors';

const NO_OP = () => {};

const runFeedSelector = (runSetRef: RunSetRef) =>
  createSelector(makeRunSetSelector(runSetRef), runSet => runSet.runFeed);

type TableSettingsUpdater = (
  updaterFn: (current: ConfigV2) => ConfigV2
) => void;

type RunSelectorTableSettingsContextType = readonly [
  ConfigV2,
  TableSettingsUpdater,
];

const RunSelectorTableSettingsContext =
  createContext<RunSelectorTableSettingsContextType>([
    EMPTY_TABLE_SETTINGS,
    NO_OP,
  ]);

type RunSelectorTableSettingsProviderProps = {
  children: ReactNode;
  runSetRef: RunSetRef;
};

export function RunSelectorTableSettingsProvider({
  children,
  runSetRef,
}: RunSelectorTableSettingsProviderProps) {
  // RunFeed is synonymous with TableSettings in this context
  const runFeed = useMemoizedSelector(runFeedSelector, runSetRef);

  const runSetUpdate = useViewAction(runSetRef, updateRunSet);

  const setTableSettings: TableSettingsUpdater = useCallback(
    updaterFn => runSetUpdate({runFeed: updaterFn(runFeed)}),
    [runFeed, runSetUpdate]
  );

  const value = useMemo(
    () => [runFeed, setTableSettings] as const,
    [runFeed, setTableSettings]
  );

  return (
    <RunSelectorTableSettingsContext.Provider value={value}>
      {children}
    </RunSelectorTableSettingsContext.Provider>
  );
}

export function useTableSettings() {
  const [tableSettings] = useContext(RunSelectorTableSettingsContext);
  return tableSettings;
}

export function useSetTableSettings() {
  const [, setTableSettings] = useContext(RunSelectorTableSettingsContext);
  return setTableSettings;
}
